import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useDojos } from '@/lib/queries';
import { adminLinks } from '@/lib/links';
import { shouldSearch, useDebounced, useSearch } from '@/lib/search';
import type { Audience, AudienceRow } from '@/lib/email';
import { ErrorNote, Spinner } from '@/components/Layout';

const keyOf = (a: Audience) => a.kind === 'all_current' ? 'all_current' : a.kind === 'people' ? `people:${a.label ?? ''}:${a.person_ids.length}` : `${a.kind}:${a.id}`;

/**
 * Who an admin email goes to, editable: groups (a household, a person, a dojo, all
 * current members, a filtered list) added by search or from the menu, and any single
 * address removed. Sent as the one group when nothing was changed, otherwise as the
 * list of people it comes to, which the database turns back into the same addresses.
 */
export function useRecipients(given: Audience | null) {
  const [parts, setParts] = useState<Audience[]>(given ? [given] : []);
  const [removed, setRemoved] = useState<string[]>([]);   // addresses taken off by hand
  const results = useQueries({
    queries: parts.map(a => ({
      queryKey: ['email-audience', 'admin_email_audience', a],
      queryFn: async () => {
        const { data, error } = await supabase.rpc('admin_email_audience', { p_audience: a });
        if (error) throw error;
        return (data ?? []) as AudienceRow[];
      },
    })),
  });
  const names = usePartNames(parts);
  const loading = results.some(r => r.isLoading);
  const error = results.some(r => r.error);
  const rows = useMemo(() => {
    const seen = new Map<string, AudienceRow>();
    for (const r of results) for (const row of r.data ?? []) if (!seen.has(row.to_email)) seen.set(row.to_email, row);
    return [...seen.values()].filter(r => !removed.includes(r.to_email));
  }, [results, removed]);
  const audience: Audience | null = !parts.length ? null
    : parts.length === 1 && !removed.length ? parts[0]!
    : rows.length ? { kind: 'people', person_ids: [...new Set(rows.map(r => r.person_id))],
        label: parts.map(a => names.get(keyOf(a))?.label).filter(Boolean).join(', ') + (removed.length ? ` (less ${removed.length})` : '') } : null;
  return {
    parts, rows, audience, loading, error, removed, names,
    add: (a: Audience) => setParts(p => p.some(x => keyOf(x) === keyOf(a)) ? p : [...p, a]),
    dropPart: (a: Audience) => setParts(p => p.filter(x => keyOf(x) !== keyOf(a))),
    remove: (email: string) => setRemoved(r => [...r, email]),
    restore: () => setRemoved([]),
  };
}

/** The editable To field. */
export function RecipientEditor({ r, note }: { r: ReturnType<typeof useRecipients>; note: string }) {
  const dojos = useDojos();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const dq = useDebounced(q, 150);
  const found = useSearch(dq, { limit: 12, perKind: 5 });
  const options = (found.data ?? []).filter(x => x.kind === 'person' || x.kind === 'household' || x.kind === 'dojo');
  const names = r.names;
  const [addingGroup, setAddingGroup] = useState(false);
  const shown = showAll ? r.rows : r.rows.slice(0, 8);

  return (
    <div className="space-y-3">
      {r.parts.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {r.parts.map(a => (
            <span key={keyOf(a)} className="inline-flex items-center gap-1 rounded-sm border border-ink-400 bg-white py-1 pl-2.5 pr-1 text-sm font-semibold">
              {names.get(keyOf(a))?.href ? <Link to={names.get(keyOf(a))!.href!} className="hover:underline">{names.get(keyOf(a))!.label}</Link> : names.get(keyOf(a))?.label ?? '…'}
              <button type="button" className="rounded-sm p-0.5 text-ink-500 hover:bg-stone-100 hover:text-ink-900" aria-label={`Remove ${names.get(keyOf(a))?.label ?? 'group'}`} onClick={() => r.dropPart(a)}><X size={14} /></button>
            </span>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-start gap-2">
        <div className="relative min-w-[240px] flex-1">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <input className="input pl-9" value={q} onChange={e => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)} onKeyDown={e => { if (e.key === 'Escape') { setQ(''); setOpen(false); } }}
            placeholder="Add a member, household or dojo" aria-label="Add recipients" />
          {open && shouldSearch(q) && (
            <ul className="card absolute left-0 right-0 top-full z-20 mt-1 max-h-72 overflow-y-auto p-0 py-1 shadow-lg">
              {(found.isLoading || found.isPlaceholderData) && <li className="px-4 py-2 text-sm text-ink-500">Searching…</li>}
              {!found.isLoading && !found.isPlaceholderData && !options.length && <li className="px-4 py-2 text-sm text-ink-500">No member, household or dojo matches “{q.trim()}”.</li>}
              {options.map(o => (
                <li key={`${o.kind}-${o.id}`}>
                  <button type="button" className="block w-full px-4 py-2 text-left hover:bg-stone-100" onClick={() => { r.add({ kind: o.kind as 'person' | 'household' | 'dojo', id: o.id }); setQ(''); }}>
                    <span className="block text-[15px] font-semibold">{o.title} <span className="font-normal text-ink-500">· {o.kind === 'person' ? 'Member' : o.kind === 'household' ? 'Household' : 'Everyone at this dojo'}</span></span>
                    {o.subtitle && <span className="block text-sm text-ink-500">{o.subtitle}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <select className="input w-auto" aria-label="Add a group" value="" disabled={addingGroup} onChange={async e => {
          const v = e.target.value;
          if (v === 'all_current') r.add({ kind: 'all_current' });
          else if (v === 'instructors' || v === 'lead') {
            setAddingGroup(true);
            let req = supabase.from('dojo_instructor').select('person_id').eq('active', true);
            if (v === 'lead') req = req.eq('role', 'chief');
            const { data } = await req;
            const ids = [...new Set((data ?? []).map(x => x.person_id as string))];
            if (ids.length) r.add({ kind: 'people', person_ids: ids, label: v === 'lead' ? 'Lead instructors' : 'All instructors' });
            setAddingGroup(false);
          } else if (v) r.add({ kind: 'dojo', id: v });
        }}>
          <option value="">{addingGroup ? 'Adding…' : 'Add a group…'}</option>
          <option value="all_current">All current members</option>
          <option value="instructors">All instructors</option>
          <option value="lead">Lead instructors</option>
          <optgroup label="Everyone at a dojo">{(dojos.data ?? []).map(d => <option key={d.id} value={d.id}>{d.short_name ?? d.name}</option>)}</optgroup>
        </select>
      </div>

      {r.loading ? <Spinner label="Working out who this goes to" />
        : r.error ? <ErrorNote>Could not work out who this goes to.</ErrorNote>
        : r.parts.length > 0 && (
          <div className="space-y-2">
            <p className="text-[15px]"><strong>{r.rows.length} {r.rows.length === 1 ? 'recipient' : 'recipients'}</strong>
              {r.removed.length > 0 && <> · {r.removed.length} removed <button type="button" className="btn-small ml-1" onClick={r.restore}>Put back</button></>}</p>
            <ul className="flex flex-wrap gap-1.5">
              {shown.map(x => (
                <li key={x.to_email} className="inline-flex items-center gap-1 rounded-sm bg-stone-100 py-0.5 pl-2 pr-0.5 text-sm">
                  <Link to={adminLinks.person(x.person_id)} className="hover:underline"><span className="font-semibold">{x.first_name ?? x.to_email}</span> <span className="text-ink-500">{x.to_email}</span></Link>
                  <button type="button" className="rounded-sm p-0.5 text-ink-500 hover:bg-white hover:text-ink-900" aria-label={`Remove ${x.to_email}`} onClick={() => r.remove(x.to_email)}><X size={13} /></button>
                </li>
              ))}
              {r.rows.length > shown.length && <li><button type="button" className="btn-small" onClick={() => setShowAll(true)}>Show all {r.rows.length}</button></li>}
            </ul>
            <p className="text-sm text-ink-500">{note}</p>
          </div>
        )}
      {!r.parts.length && <p className="text-sm text-ink-500">Search for a member, household or dojo, or add a group.</p>}
    </div>
  );
}

/** A readable, linked name for each group in the To field. */
function usePartNames(parts: Audience[]) {
  const dojos = useDojos();
  const lookups = useQueries({
    queries: parts.filter(a => a.kind === 'person' || a.kind === 'household').map(a => ({
      queryKey: ['audience-name', keyOf(a)],
      staleTime: 60_000,
      queryFn: async () => {
        const x = a as { kind: 'person' | 'household'; id: string };
        if (x.kind === 'person') {
          const { data } = await supabase.from('person').select('first_name,last_name').eq('id', x.id).maybeSingle();
          return { key: keyOf(a), label: data ? `${data.first_name} ${data.last_name}` : 'A member', href: adminLinks.person(x.id) };
        }
        const { data } = await supabase.from('household').select('name').eq('id', x.id).maybeSingle();
        return { key: keyOf(a), label: data?.name ?? 'A household', href: adminLinks.household(x.id) };
      },
    })),
  });
  const map = new Map<string, { label: string; href?: string }>();
  for (const l of lookups) if (l.data) map.set(l.data.key, l.data);
  for (const a of parts) {
    if (a.kind === 'all_current') map.set(keyOf(a), { label: 'All current members' });
    if (a.kind === 'people') map.set(keyOf(a), { label: a.label ?? `${a.person_ids.length} ${a.person_ids.length === 1 ? 'person' : 'people'} from the members list` });
    if (a.kind === 'dojo') { const d = dojos.data?.find(x => x.id === a.id); map.set(keyOf(a), { label: d ? `Everyone at ${d.short_name ?? d.name}` : 'A dojo', href: adminLinks.dojo(a.id) }); }
  }
  return map;
}
